/* ==========================================================================
   SUMO Kitchenware — theme.js
   Vanilla, no framework, no build step. Loaded with `defer`.
   Modules: utils, CartAPI, CartDrawer, MobileMenu, QuantityStepper, Accordion.
   Gallery / variant / reviews modules are registered by their sections and
   initialised here when present.
   ========================================================================== */
(function () {
  'use strict';

  /* ----- utils ----- */
  var $ = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };
  var on = function (el, ev, fn, opts) { if (el) el.addEventListener(ev, fn, opts); };

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, opener) {
    var nodes = $$(FOCUSABLE, container).filter(function (n) { return n.offsetParent !== null; });
    if (!nodes.length) return function () {};
    var first = nodes[0], last = nodes[nodes.length - 1];
    function handle(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', handle);
    first.focus();
    return function () {
      container.removeEventListener('keydown', handle);
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
  }

  function formatMoney(cents) {
    // USD single-market. Replace with localization-aware formatting if markets change.
    return '$' + (cents / 100).toFixed(2);
  }

  /* ----- Cart API ----- */
  var CartAPI = {
    add: function (body) {
      return fetch((window.routes ? window.routes.cart_add_url : '/cart/add') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      }).then(parse);
    },
    change: function (body) {
      return fetch((window.routes ? window.routes.cart_change_url : '/cart/change') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      }).then(parse);
    },
    get: function () {
      return fetch((window.routes ? window.routes.cart_url : '/cart') + '.js', { headers: { 'Accept': 'application/json' } }).then(parse);
    }
  };
  function parse(res) {
    return res.json().then(function (data) {
      if (!res.ok) { throw data; }
      return data;
    });
  }

  function updateCartCount(count) {
    $$('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  /* ----- Cart Drawer ----- */
  var CartDrawer = {
    el: null, releaseFocus: null,
    init: function () {
      this.el = $('[data-cart-drawer]');
      if (!this.el) return;
      var self = this;
      $$('[data-cart-toggle]').forEach(function (btn) {
        on(btn, 'click', function (e) { e.preventDefault(); self.open(btn); });
      });
      on($('[data-cart-close]', this.el), 'click', function () { self.close(); });
      on($('[data-cart-overlay]', this.el), 'click', function () { self.close(); });
      on(document, 'keydown', function (e) { if (e.key === 'Escape' && self.el.classList.contains('is-open')) self.close(); });
      // delegate qty +/- , remove, and qty input change inside the drawer (survives re-render)
      on(this.el, 'click', function (e) {
        var rm = e.target.closest('[data-cart-remove]');
        if (rm) { e.preventDefault(); self.changeLine(rm.getAttribute('data-cart-remove'), 0); return; }
        var minus = e.target.closest('[data-cart-minus]');
        if (minus) { e.preventDefault(); self.stepLine(minus.getAttribute('data-cart-minus'), -1); return; }
        var plus = e.target.closest('[data-cart-plus]');
        if (plus) { e.preventDefault(); self.stepLine(plus.getAttribute('data-cart-plus'), 1); return; }
        var xadd = e.target.closest('[data-cross-add]');
        if (xadd) {
          e.preventDefault();
          xadd.disabled = true;
          CartAPI.add({ id: xadd.getAttribute('data-cross-add'), quantity: 1 }).then(function (cart) {
            updateCartCount(cart.item_count); self.render(cart);
          }).catch(function () { xadd.disabled = false; self.notifyError(); });
          return;
        }
      });
      on(this.el, 'change', function (e) {
        var input = e.target.closest('[data-cart-qty]');
        if (input) { self.changeLine(input.getAttribute('data-cart-qty'), Math.max(0, parseInt(input.value, 10) || 0)); }
      });
    },
    open: function (opener) {
      if (!this.el) return;
      this.el.classList.add('is-open');
      this.el.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cart-open');
      this.releaseFocus = trapFocus($('[data-cart-panel]', this.el) || this.el, opener);
    },
    close: function () {
      if (!this.el) return;
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-open');
      if (this.releaseFocus) { this.releaseFocus(); this.releaseFocus = null; }
    },
    stepLine: function (key, dir) {
      var input = $('[data-cart-qty="' + key + '"]', this.el);
      var current = input ? (parseInt(input.value, 10) || 0) : 0;
      this.changeLine(key, Math.max(0, current + dir));
    },
    changeLine: function (key, qty) {
      var self = this;
      CartAPI.change({ id: key, quantity: qty }).then(function (cart) {
        updateCartCount(cart.item_count);
        self.render(cart);
      }).catch(function () { self.notifyError(); });
    },
    refresh: function () {
      var self = this;
      return CartAPI.get().then(function (cart) { updateCartCount(cart.item_count); return self.render(cart); });
    },
    render: function () {
      // Re-render via the section rendering API for markup fidelity.
      // Guard on the always-present wrapper, NOT [data-cart-items] (absent in the
      // empty state) — otherwise the first add to an empty cart never updates.
      if (!this.el) return Promise.resolve();
      return fetch('?section_id=cart-drawer', { cache: 'no-store' }).then(function (r) { return r.text(); }).then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-cart-contents]');
        var current = $('[data-cart-contents]', CartDrawer.el);
        if (fresh && current) current.replaceWith(fresh);
      }).catch(function () {});
    },
    notifyError: function () {
      var n = $('[data-cart-error]', this.el);
      if (n) { n.textContent = (window.cartStrings && window.cartStrings.error) || 'Error'; n.hidden = false; }
    }
  };

  /* ----- Mobile menu ----- */
  var MobileMenu = {
    init: function () {
      var toggle = $('[data-menu-toggle]');
      var drawer = $('[data-menu-drawer]');
      if (!toggle || !drawer) return;
      var release = null;
      function open() {
        drawer.classList.add('is-open'); drawer.setAttribute('aria-hidden', 'false');
        toggle.setAttribute('aria-expanded', 'true'); document.body.classList.add('menu-open');
        release = trapFocus(drawer, toggle);
      }
      function close() {
        drawer.classList.remove('is-open'); drawer.setAttribute('aria-hidden', 'true');
        toggle.setAttribute('aria-expanded', 'false'); document.body.classList.remove('menu-open');
        if (release) { release(); release = null; }
      }
      on(toggle, 'click', function () { drawer.classList.contains('is-open') ? close() : open(); });
      on($('[data-menu-close]', drawer), 'click', close);
      on(document, 'keydown', function (e) { if (e.key === 'Escape' && drawer.classList.contains('is-open')) close(); });
    }
  };

  /* ----- Quantity steppers ----- */
  var QuantityStepper = {
    init: function (ctx) {
      $$('[data-qty]', ctx).forEach(function (wrap) {
        var input = $('input', wrap);
        on($('[data-qty-down]', wrap), 'click', function () { step(input, -1); });
        on($('[data-qty-up]', wrap), 'click', function () { step(input, 1); });
      });
      function step(input, dir) {
        if (!input) return;
        var min = parseInt(input.min || '1', 10);
        var val = (parseInt(input.value, 10) || min) + dir;
        input.value = Math.max(min, val);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  };

  /* ----- Accordion (FAQ / collapsible) — native <details> needs no JS, but sync aria + single-open option ----- */
  var Accordion = {
    init: function () {
      $$('[data-accordion-single]').forEach(function (group) {
        var items = $$('details', group);
        items.forEach(function (d) {
          on(d, 'toggle', function () {
            if (d.open) items.forEach(function (o) { if (o !== d) o.open = false; });
          });
        });
      });
    }
  };

  /* ----- Gallery (shared by product module) ----- */
  function Gallery(root) {
    var slides = $$('[data-slide]', root);
    var thumbs = $$('[data-thumb]', root);
    var dots = $$('[data-dot]', root);
    var promoted = false;

    function promoteLazy() {
      if (promoted) return; promoted = true;
      $$('[data-lazy]', root).forEach(function (img) {
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        if (img.dataset.src) img.src = img.dataset.src;
        img.removeAttribute('data-lazy');
      });
    }
    function show(mediaId) {
      mediaId = String(mediaId);
      slides.forEach(function (s) { s.classList.toggle('is-active', s.getAttribute('data-media-id') === mediaId); });
      thumbs.forEach(function (t) { t.classList.toggle('is-active', t.getAttribute('data-media-id') === mediaId); });
      dots.forEach(function (d) { d.classList.toggle('is-active', d.getAttribute('data-media-id') === mediaId); });
    }
    thumbs.forEach(function (t) {
      on(t, 'click', function () {
        var img = $('.product__slide[data-media-id="' + t.getAttribute('data-media-id') + '"] [data-main-image]', root);
        // ⚠️ set .srcset BEFORE .src or the browser keeps the stale srcset
        if (img && t.dataset.mainSrcset) { img.srcset = t.dataset.mainSrcset; img.src = t.dataset.mainSrc; img.removeAttribute('data-lazy'); }
        show(t.getAttribute('data-media-id'));
      });
    });
    dots.forEach(function (d) { on(d, 'click', function () { promoteLazy(); show(d.getAttribute('data-media-id')); }); });
    // Variant-aware: show only media for the selected colour + shared (no-variant) media.
    function visibleFor(el, color) {
      return el.getAttribute('data-shared') === 'true' || el.getAttribute('data-media-color') === color;
    }
    function filter(color) {
      slides.forEach(function (s) { s.classList.toggle('is-vhidden', !visibleFor(s, color)); });
      thumbs.forEach(function (t) { t.classList.toggle('is-vhidden', !visibleFor(t, color)); });
      dots.forEach(function (d) { d.classList.toggle('is-vhidden', !visibleFor(d, color)); });
    }
    function step(dir) {
      promoteLazy();
      var vis = slides.filter(function (s) { return !s.classList.contains('is-vhidden'); });
      if (!vis.length) return;
      var i = 0;
      for (var k = 0; k < vis.length; k++) { if (vis[k].classList.contains('is-active')) { i = k; break; } }
      var n = (i + dir + vis.length) % vis.length;
      show(vis[n].getAttribute('data-media-id'));
    }
    var prevBtn = $('[data-gallery-prev]', root); if (prevBtn) on(prevBtn, 'click', function () { step(-1); });
    var nextBtn = $('[data-gallery-next]', root); if (nextBtn) on(nextBtn, 'click', function () { step(1); });
    on(root, 'touchstart', promoteLazy, { passive: true });
    if ('requestIdleCallback' in window) { requestIdleCallback(promoteLazy); } else { window.addEventListener('load', promoteLazy); }

    return { show: show, filter: filter };
  }

  /* ----- Product (variants + ATC) ----- */
  SumoTheme_registerProduct();
  function SumoTheme_registerProduct() {
    // registered onto SumoTheme.modules after SumoTheme exists (see bottom)
    productModule = function (ctx) {
      var section = $('[data-product-section]', ctx || document);
      if (!section) return;
      var variants = [];
      var json = $('[data-variant-json]', section);
      if (json) { try { variants = JSON.parse(json.textContent); } catch (e) {} }

      var form = $('#product-form', section);
      var idInput = $('[data-variant-id]', section);
      var priceBox = $('[data-price-container]', section);
      var atc = $('[data-atc]', section);
      var atcText = $('[data-atc-text]', section);
      var atcError = $('[data-atc-error]', section);
      var stickyPrice = $('[data-sticky-price]', section);
      var gallery = Gallery($('[data-gallery]', section) || section);

      var STR = {
        add: section.getAttribute('data-str-add'),
        sold: section.getAttribute('data-str-sold'),
        unavailable: section.getAttribute('data-str-unavailable'),
        adding: section.getAttribute('data-str-adding')
      };

      function selected() { return $$('[data-option-input]:checked', section).map(function (i) { return i.value; }); }
      function match(opts) {
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          var ok = true;
          for (var j = 0; j < opts.length; j++) { if (v.options[j] !== opts[j]) { ok = false; break; } }
          if (ok) return v;
        }
        return null;
      }

      function update() {
        var opts = selected();
        if (opts[0]) {
          var ch = opts[0].toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
          section.style.setProperty('--accent', 'var(--color-product-' + ch + ')');
          document.documentElement.style.setProperty('--variant-accent', 'var(--color-product-' + ch + ')');
          gallery.filter(ch);
        }
        $$('[data-option-selected]', section).forEach(function (el) {
          var pos = parseInt(el.getAttribute('data-option-selected'), 10);
          el.textContent = opts[pos - 1] || '';
        });
        var v = match(opts);
        if (!v) { atc.disabled = true; atcText.textContent = STR.unavailable; return; }
        idInput.value = v.id;
        atc.disabled = !v.available;
        atcText.textContent = v.available ? STR.add : STR.sold;
        if (priceBox) {
          var amt = $('[data-price-amount]', priceBox);
          if (amt) amt.textContent = formatMoney(v.price);
          var cmp = $('[data-price-compare]', priceBox);
          if (cmp) { if (v.compare_at_price > v.price) { cmp.textContent = formatMoney(v.compare_at_price); cmp.hidden = false; } else { cmp.hidden = true; } }
        }
        if (stickyPrice) stickyPrice.textContent = formatMoney(v.price);
        if (v.featured_media) gallery.show(v.featured_media.id);
        try { history.replaceState({}, '', updateParam('variant', v.id)); } catch (e) {}
      }

      function updateParam(key, val) {
        var u = new URL(window.location.href); u.searchParams.set(key, val); return u.toString();
      }

      $$('[data-option-input]', section).forEach(function (i) { on(i, 'change', update); });

      if (form) {
        on(form, 'submit', function (e) {
          e.preventDefault();
          if (atc.disabled) return;
          atc.disabled = true;
          var original = atcText.textContent;
          atcText.textContent = STR.adding;
          if (atcError) atcError.hidden = true;
          var qtyInput = $('input[name="quantity"]', form);
          CartAPI.add({ items: [{ id: parseInt(idInput.value, 10), quantity: parseInt(qtyInput ? qtyInput.value : 1, 10) }] })
            .then(function () { return CartDrawer.refresh(); })
            .then(function () { atc.disabled = false; atcText.textContent = original; CartDrawer.open(atc); })
            .catch(function (err) {
              atc.disabled = false; atcText.textContent = original;
              if (atcError) { atcError.textContent = (err && err.description) || (window.cartStrings && window.cartStrings.error) || 'Error'; atcError.hidden = false; }
            });
        });
      }

      // Sticky ATC visibility
      var sticky = $('[data-sticky-atc]', section);
      if (sticky && atc && 'IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) { sticky.classList.toggle('is-visible', !en.isIntersecting); sticky.setAttribute('aria-hidden', en.isIntersecting ? 'true' : 'false'); });
        }, { rootMargin: '0px 0px -80px 0px' });
        io.observe(atc);
      }

      update();
    };
  }
  var productModule;

  /* ----- Module registry for section-specific behaviours ----- */
  window.SumoTheme = {
    $: $, $$: $$, on: on, trapFocus: trapFocus, formatMoney: formatMoney,
    CartAPI: CartAPI, CartDrawer: CartDrawer, QuantityStepper: QuantityStepper,
    updateCartCount: updateCartCount,
    modules: {},
    register: function (name, fn) { this.modules[name] = fn; },
    init: function (name, ctx) { if (this.modules[name]) this.modules[name](ctx); }
  };

  function LogoEasterEgg() {
    var logo = $('[data-logo-jiggle]');
    if (!logo) return;
    on(logo, 'animationend', function () { logo.classList.remove('is-jiggling'); });
    on(logo, 'click', function (e) {
      // Replay the pop. If already on the homepage, stay put so it's visible.
      var onHome = location.pathname === '/' || location.pathname === '';
      if (onHome) e.preventDefault();
      logo.classList.remove('is-jiggling');
      // force reflow so the animation restarts
      void logo.offsetWidth;
      logo.classList.add('is-jiggling');
    });
  }

  function boot() {
    CartDrawer.init();
    MobileMenu.init();
    LogoEasterEgg();
    QuantityStepper.init(document);
    Accordion.init();
    if (productModule) window.SumoTheme.register('product', productModule);
    Object.keys(window.SumoTheme.modules).forEach(function (name) { window.SumoTheme.init(name, document); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else { boot(); }
})();
